import type { CompanionFieldMode } from "@/lib/companionFields";
import {
  DEFAULT_VEHICLE_FIELDS,
  DEFAULT_VEHICLE_FIELDS_BY_CODE,
  NUMERIC_VEHICLE_FIELD_KEYS,
  VEHICLE_FIELD_ROWS,
  type VehicleFieldKey,
  type VehicleFieldTextConfig,
  type VehicleFieldsConfig,
} from "@/lib/vehicleFields";

export type VehicleFieldEditorState = {
  modes: VehicleFieldsConfig;
  tooltips: VehicleFieldTextConfig;
  max_values: VehicleFieldTextConfig;
  max_messages: VehicleFieldTextConfig;
};

type Props = {
  code: string;
  value: VehicleFieldEditorState;
  onChange: (value: VehicleFieldEditorState) => void;
  disabled?: boolean;
  touristIntegrationEnabled?: boolean;
};

const MODE_OPTIONS: { value: CompanionFieldMode; label: string }[] = [
  { value: "off", label: "Aus" },
  { value: "optional", label: "Optional" },
  { value: "required", label: "Pflicht" },
];

export function resolveVehicleFieldModes(
  code: string,
  stored: Partial<VehicleFieldsConfig> | null | undefined,
): VehicleFieldsConfig {
  const defaults = {
    ...DEFAULT_VEHICLE_FIELDS,
    ...(DEFAULT_VEHICLE_FIELDS_BY_CODE[code] ?? {}),
  };
  if (!stored) return defaults;
  return { ...defaults, ...stored };
}

function resolveTextConfig(
  stored: VehicleFieldTextConfig | null | undefined,
): VehicleFieldTextConfig {
  if (!stored) return {};
  return { ...stored };
}

export function resolveVehicleFieldEditorState(
  code: string,
  record: {
    vehicle_field_modes?: Partial<VehicleFieldsConfig> | null;
    vehicle_field_tooltips?: VehicleFieldTextConfig | null;
    vehicle_field_max_values?: VehicleFieldTextConfig | null;
    vehicle_field_max_messages?: VehicleFieldTextConfig | null;
  },
): VehicleFieldEditorState {
  return {
    modes: resolveVehicleFieldModes(code, record.vehicle_field_modes),
    tooltips: resolveTextConfig(record.vehicle_field_tooltips),
    max_values: resolveTextConfig(record.vehicle_field_max_values),
    max_messages: resolveTextConfig(record.vehicle_field_max_messages),
  };
}

function updateTextConfig(
  current: VehicleFieldTextConfig,
  key: VehicleFieldKey,
  value: string,
): VehicleFieldTextConfig {
  const trimmed = value.trim();
  if (!trimmed) {
    const next = { ...current };
    delete next[key];
    return next;
  }
  return { ...current, [key]: trimmed };
}

export function VehicleFieldModesEditor({
  code,
  value,
  onChange,
  disabled,
  touristIntegrationEnabled = false,
}: Props) {
  const modes = resolveVehicleFieldModes(code, value.modes);

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-label">Fahrzeugfelder (Online-Buchung)</p>
      {touristIntegrationEnabled ? (
        <p className="text-xs text-label-secondary">
          Bei aktiver Tourist-Anbindung können Sie pro Feld Maximalwerte und einen Hinweistext
          festlegen, der erscheint, wenn der Wert überschritten wird.
        </p>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-separator">
        <table className="w-full min-w-[48rem] text-left text-sm">
          <thead className="border-b border-separator bg-fill-tertiary text-label-secondary">
            <tr>
              <th className="px-3 py-2 font-medium">Feld</th>
              <th className="px-3 py-2 font-medium">Einstellung</th>
              <th className="px-3 py-2 font-medium">Tooltip</th>
              {touristIntegrationEnabled ? (
                <>
                  <th className="px-3 py-2 font-medium">Maximalwert</th>
                  <th className="px-3 py-2 font-medium">Hinweis bei Überschreitung</th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {VEHICLE_FIELD_ROWS.map((row) => {
              const isNumeric = NUMERIC_VEHICLE_FIELD_KEYS.includes(row.key);
              const fieldActive = modes[row.key] !== "off";
              return (
                <tr key={row.key} className="border-b border-separator last:border-0">
                  <td className="px-3 py-2 text-label">{row.label}</td>
                  <td className="px-3 py-2">
                    <select
                      className="eh-select-input w-full max-w-[10rem]"
                      disabled={disabled}
                      value={modes[row.key]}
                      onChange={(e) =>
                        onChange({
                          ...value,
                          modes: {
                            ...modes,
                            [row.key]: e.target.value as CompanionFieldMode,
                          },
                        })
                      }
                    >
                      {MODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className="eh-input w-full min-w-[12rem]"
                      disabled={disabled || !fieldActive}
                      value={value.tooltips[row.key] ?? ""}
                      placeholder={
                        row.key === "license_plate"
                          ? "z. B. Format AB-C 1234 – Leerzeichen …"
                          : row.key === "vehicle_length_m"
                            ? "z. B. Gesamtlänge inkl. Anhänger …"
                            : "Optionaler Hilfetext"
                      }
                      onChange={(e) =>
                        onChange({
                          ...value,
                          tooltips: updateTextConfig(value.tooltips, row.key, e.target.value),
                        })
                      }
                    />
                  </td>
                  {touristIntegrationEnabled ? (
                    <>
                      <td className="px-3 py-2">
                        {isNumeric ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            className="eh-input w-full max-w-[8rem]"
                            disabled={disabled || !fieldActive}
                            value={value.max_values[row.key] ?? ""}
                            placeholder="z. B. 8,5"
                            onChange={(e) =>
                              onChange({
                                ...value,
                                max_values: updateTextConfig(
                                  value.max_values,
                                  row.key,
                                  e.target.value,
                                ),
                              })
                            }
                          />
                        ) : (
                          <span className="text-xs text-label-secondary">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isNumeric ? (
                          <input
                            type="text"
                            className="eh-input w-full min-w-[12rem]"
                            disabled={disabled || !fieldActive}
                            value={value.max_messages[row.key] ?? ""}
                            placeholder="Text bei Überschreitung des Maximalwerts"
                            onChange={(e) =>
                              onChange({
                                ...value,
                                max_messages: updateTextConfig(
                                  value.max_messages,
                                  row.key,
                                  e.target.value,
                                ),
                              })
                            }
                          />
                        ) : (
                          <span className="text-xs text-label-secondary">—</span>
                        )}
                      </td>
                    </>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export type { VehicleFieldKey };
